import { useRef, useState, type MouseEvent } from 'react';
import { Button, Dropdown } from 'antd';
import { CaretLeftOutlined, MoreOutlined } from '@ant-design/icons';
import TruncatedText from '../TruncatedText';
import styles from './RowOptionsMenu.module.scss';

export interface RowOptionPanel {
  header: string;
  options: RowOptionItem[];
  actionId?: string;
  showBack?: boolean;
  /** Options become checkboxes, confirmed together via `onMultiSelect`. */
  multiSelect?: boolean;
}

export interface RowOptionItem {
  id: string;
  label: string;
  panel?: RowOptionPanel;
}

interface RowOptionsMenuProps {
  options: RowOptionItem[];
  /** Panel heading, and the accessible name for both the trigger and the menu. */
  header: string;
  /** `trigger` lets the caller anchor follow-up UI to this menu's button. */
  onSelect: (actionId: string, trigger: HTMLElement | null, parentActionId?: string) => void;
  /** `parentActionId` tells apart several multi-select panels sharing one handler. */
  onMultiSelect?: (actionIds: string[], parentActionId?: string) => void;
}

interface MenuState {
  open: boolean;
  activePanel: RowOptionPanel | null;
  panelHistory: RowOptionPanel[];
  checkedIds: string[];
}

const INITIAL_MENU: MenuState = { open: false, activePanel: null, panelHistory: [], checkedIds: [] };

const RowOptionsMenu = ({ options, header, onSelect, onMultiSelect }: RowOptionsMenuProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menu, setMenu] = useState<MenuState>(INITIAL_MENU);
  const { open, activePanel, checkedIds } = menu;

  const closeMenu = () => setMenu(INITIAL_MENU);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setMenu((current) => ({ ...current, open: true }));
    else closeMenu();
  };

  const handleBack = () =>
    setMenu((current) => ({
      ...current,
      activePanel: current.panelHistory[current.panelHistory.length - 1] ?? null,
      panelHistory: current.panelHistory.slice(0, -1),
      checkedIds: [],
    }));

  const handleSelect = (item: RowOptionItem) => {
    if (item.panel) {
      const nextPanel = item.panel;
      setMenu((current) => ({
        ...current,
        activePanel: nextPanel,
        panelHistory: current.activePanel ? [...current.panelHistory, current.activePanel] : current.panelHistory,
      }));
      return;
    }

    if (activePanel?.multiSelect) {
      setMenu((current) => ({
        ...current,
        checkedIds: current.checkedIds.includes(item.id) ? current.checkedIds.filter((id) => id !== item.id) : [...current.checkedIds, item.id],
      }));
      return;
    }

    onSelect(item.id, triggerRef.current, activePanel?.actionId);
    closeMenu();
  };

  const handleConfirm = () => {
    onMultiSelect?.(checkedIds, activePanel?.actionId);
    closeMenu();
  };

  const handleBackClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleBack();
  };

  const handleConfirmClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleConfirm();
  };

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => event.stopPropagation();

  const panelHeader = activePanel?.header ?? header;
  const panelOptions = activePanel?.options ?? options;
  const isMultiSelect = Boolean(activePanel?.multiSelect);

  const handleItemClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const item = panelOptions.find((option) => option.id === event.currentTarget.dataset.id);
    if (item) handleSelect(item);
  };

  const renderPanel = () => (
    <div className={`${styles.panel} ${isMultiSelect ? styles.panelLarge : ''}`}>
      <div className={styles.header}>
        {activePanel?.showBack ? (
          <button type="button" className={styles.backButton} aria-label="Back to previous options" onClick={handleBackClick}>
            <CaretLeftOutlined aria-hidden="true" />
          </button>
        ) : null}
        <span>{panelHeader}</span>
      </div>
      {/* Buttons carry activation natively, so Enter/Space need no handler
          of our own — same shape as AppHeader's user menu. */}
      <div role="menu" aria-label={panelHeader} className={activePanel ? styles.scrollList : undefined}>
        {panelOptions.map((item) => (
          // Native button: antd Button's text-hover styles outrank .item and would need specificity overrides.
          <button
            key={item.id}
            type="button"
            role={isMultiSelect ? 'menuitemcheckbox' : 'menuitem'}
            aria-checked={isMultiSelect ? checkedIds.includes(item.id) : undefined}
            className={styles.item}
            data-id={item.id}
            onClick={handleItemClick}
          >
            {isMultiSelect ? (
              <span className={`${styles.itemCheckbox} ${checkedIds.includes(item.id) ? styles.itemCheckboxChecked : ''}`} aria-hidden="true" />
            ) : null}
            <TruncatedText text={item.label} compact />
          </button>
        ))}
      </div>
      {isMultiSelect ? (
        <div className={styles.footer}>
          <Button className={styles.confirmButton} disabled={!checkedIds.length} onClick={handleConfirmClick}>
            OK
          </Button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dropdown trigger={['click']} placement="bottomRight" destroyPopupOnHide open={open} onOpenChange={handleOpenChange} dropdownRender={renderPanel}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={header}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleTriggerClick}
      >
        <MoreOutlined />
      </button>
    </Dropdown>
  );
};

export default RowOptionsMenu;
